"use client";

import { c as _c } from "react/compiler-runtime";
import type { FC } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import type { ApiWaveDecisionPause } from "@/generated/models/ApiWaveDecisionPause";
interface TimelineToggleHeaderProps {
  readonly isOpen: boolean;
  readonly setIsOpen: (isOpen: boolean) => void;
  readonly nextDecisionTime: number | null;
  readonly isPaused?: boolean | undefined;
  readonly currentPause?: ApiWaveDecisionPause | null | undefined;
}

/**
 * Renders the header for the timeline with toggle functionality
 */
export const TimelineToggleHeader: FC<TimelineToggleHeaderProps> = t0 => {
  const $ = _c(25);
  const {
    isOpen,
    setIsOpen,
    nextDecisionTime,
    isPaused: t1,
    currentPause
  } = t0;
  const isPaused = t1 === undefined ? false : t1;
  const hasNextDecision = typeof nextDecisionTime === "number";
  let t2;
  if ($[0] !== currentPause || $[1] !== hasNextDecision || $[2] !== isPaused || $[3] !== nextDecisionTime) {
    t2 = () => {
      if (isPaused && currentPause) {
        return <span className="tw-inline-flex tw-items-center tw-gap-x-2 tw-rounded-md tw-border tw-border-solid tw-border-iron-600/40 tw-bg-gradient-to-r tw-from-iron-800/90 tw-to-iron-700/70 tw-px-3 tw-py-1.5 tw-text-xs tw-shadow-sm tw-transition-all tw-duration-300 tw-ease-out group-hover:tw-border-iron-600/30 group-hover:tw-from-iron-700/60 group-hover:tw-to-iron-600/40 group-hover:tw-shadow-none sm:tw-gap-x-2.5"><span className="tw-whitespace-nowrap tw-bg-gradient-to-r tw-from-amber-200 tw-via-amber-100 tw-to-amber-200/90 tw-bg-clip-text tw-font-bold tw-text-transparent">Decisions paused</span><span className="tw-text-iron-500">•</span><span className="tw-whitespace-nowrap tw-font-medium tw-text-iron-300">{hasNextDecision ? `Next decision after ${new Date(nextDecisionTime).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric"
            })}` : "No decision scheduled"}</span></span>;
      }
      if (hasNextDecision) {
        return <span className="tw-text-xs tw-font-medium tw-text-iron-300">{new Date(nextDecisionTime).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
          })}</span>;
      }
      return <span className="tw-text-xs tw-text-iron-400">No upcoming events</span>;
    };
    $[0] = currentPause;
    $[1] = hasNextDecision;
    $[2] = isPaused;
    $[3] = nextDecisionTime;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const getStatusDisplay = t2;
  let t3;
  if ($[5] !== isOpen || $[6] !== setIsOpen) {
    t3 = () => setIsOpen(!isOpen);
    $[5] = isOpen;
    $[6] = setIsOpen;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = `tw-flex-shrink-0 tw-whitespace-nowrap tw-text-xs tw-font-semibold ${hasNextDecision ? "tw-text-iron-100" : "tw-text-iron-400"}`;
  const t5 = hasNextDecision ? "Decision Timeline" : "Announcement history";
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== getStatusDisplay) {
    t7 = <div className="tw-flex tw-flex-1 tw-items-center tw-justify-end tw-text-xs tw-font-medium">{getStatusDisplay()}</div>;
    $[11] = getStatusDisplay;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = isOpen ? "Collapse" : "Expand";
  const t9 = `tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-200 desktop-hover:group-hover:tw-text-iron-100 ${isOpen ? "tw-rotate-180" : ""} tw-transition-all tw-duration-300 tw-ease-in-out`;
  let t10;
  if ($[13] !== t9) {
    t10 = <FontAwesomeIcon icon={faChevronDown} className={t9} />;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t8) {
    t11 = <button className="tw-flex tw-h-6 tw-w-6 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-iron-600/40 tw-bg-iron-700/50 tw-transition-all tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-iron-500/50 desktop-hover:hover:tw-bg-iron-600/60" aria-label={t8}>{t10}</button>;
    $[15] = t10;
    $[16] = t8;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t6 || $[20] !== t7) {
    t12 = <div className="tw-flex tw-w-full tw-items-center tw-gap-2">{t6}{t7}{t11}</div>;
    $[18] = t11;
    $[19] = t6;
    $[20] = t7;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12 || $[23] !== t3) {
    t13 = <div className="tw-group tw-cursor-pointer tw-rounded-t-lg tw-border tw-border-solid tw-border-iron-700/50 tw-bg-iron-800/95 tw-px-3 tw-py-1.5 tw-shadow-sm tw-transition-all tw-duration-300 tw-ease-out tw-@container desktop-hover:hover:tw-bg-iron-700/80" onClick={t3}>{t12}</div>;
    $[22] = t12;
    $[23] = t3;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  return t13;
};